import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  model,
  signal,
} from '@angular/core';
import { CoinflipCoinComponent } from '../../coinflip-coin/coinflip-coin.component';
import { CoinflipCoin } from '@features/coinflip/types/coins.type';
import { Skin } from '@core/interfaces/skin.interface';
import { ModalService } from '@features/modals/services/modal.service';
import { CoinflipDeprecatedService } from '@old-app/coinflip/coinflip.service';
import { InventoryTemplateComponent } from '@core/components/inventory-template/inventory-template.component';
import { Coinflip } from '@old-app/coinflip/interfaces/coinflip.interface';
import { ToastrService } from 'ngx-toastr';
import { SvgIconComponent } from 'angular-svg-icon';
import { NgpTooltip, NgpTooltipTrigger } from 'ng-primitives/tooltip';
import { OldSliderComponent } from '@core/components/old-slider/old-slider.component';
import { CostComponent } from '@core/components/cost/cost.component';

@Component({
  selector: 'app-coinflip-deposit-modal',
  imports: [
    InventoryTemplateComponent,
    CoinflipCoinComponent,
    CostComponent,
    SvgIconComponent,
    NgpTooltip,
    NgpTooltipTrigger,
    OldSliderComponent,
  ],
  templateUrl: './coinflip-deposit-modal.component.html',
  styleUrl: './coinflip-deposit-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  host: {
    '[class.create]': '!isJoinModal()',
  },
})
export class CoinflipDepositModalComponent {
  coinflip = input<Coinflip>();
  isJoinModal = computed(() => !!this.coinflip());
  public readonly minJoinDifference = 10;
  public readonly maxJoinDifference = 50;
  readonly coin = signal<CoinflipCoin>(1);
  readonly joinDifference = signal<number>(this.minJoinDifference);
  readonly pure = model<boolean>(false);
  readonly selectedSkins = model<Skin[]>([]);
  readonly isProcessing = signal(false);

  constructor(
    private coinflipService: CoinflipDeprecatedService,
    private modalService: ModalService,
    private toastService: ToastrService
  ) {
    effect(() => {
      if (this.isJoinModal()) {
        const coinflip = this.coinflip();
        if (coinflip) {
          this.coin.set(coinflip.host.side === 1 ? 2 : 1);
        }
      }
    });

    const isPureEffect = effect(() => {
      if (this.coinflip()?.is_pure) {
        this.pure.set(true);
        isPureEffect.destroy();
      }
    });
  }

  public async createCoinflip() {
    try {
      this.isProcessing.set(true);
      const trade = await this.coinflipService.createCoinflip({
        skins: this.selectedSkins(),
        side: this.coin(),
        joinDifference: Number(this.joinDifference().toFixed()),
        isPure: this.pure(),
      });

      this.modalService.openModal('TradeModal', {
        tradeModalOptions: {
          trade_offer_id: trade.offerid,
          trade_hash: trade.hash,
          trade_skins: this.selectedSkins(),
          typography_trade_for: 'create the coinflip',
        },
        balance: this.pure() ? 'pure' : 'skins',
      });
      this.modalService.closeModals(['CoinflipDepositModal']);
    } catch {
    } finally {
      this.isProcessing.set(false);
    }
  }

  public async joinCoinflip() {
    const coinflip = this.coinflip();

    if (!coinflip) {
      this.toastService.error('No coinflip found');
      return;
    }
    this.isProcessing.set(true);
    try {
      const trade = await this.coinflipService.joinCoinflip(
        coinflip.gameid,
        this.selectedSkins()
      );
      this.modalService.openModal('TradeModal', {
        tradeModalOptions: {
          trade_offer_id: trade.offerid,
          trade_hash: trade.hash,
          trade_skins: this.selectedSkins(),
          typography_trade_for: 'join the coinflip',
        },
        balance: this.pure() ? 'pure' : 'skins',
      });
      this.modalService.closeModals(['CoinflipDepositModal']);
    } catch {
    } finally {
      this.isProcessing.set(false);
    }
  }

  public setCoin(coin: CoinflipCoin) {
    if (this.isJoinModal()) {
      this.toastService.error('You cannot chose the side you are joining');
      return;
    }

    this.coin.set(coin);
  }
}
